<template>
  <div class="cs-wizard-host">
    <wizard-shell
      :value="value"
      icon="mdi-table-merge-cells"
      title="Composite source"
      close-label="Close composite source editor"
      :subtitle="target ? target.name : ''"
      :steps="steps"
      :current-step="currentStep"
      @input="$emit('input', $event)"
      @close="close">
      <div v-if="factoryError" class="cs-wizard__blocked">
        <v-icon size="32" color="error">mdi-alert-circle-outline</v-icon>
        <div class="cs-wizard__blocked-title">This source cannot be opened here</div>
        <div class="cs-wizard__blocked-text">{{ factoryError }}</div>
      </div>

      <template v-else-if="activeStepComponent && model">
        <div v-if="operationFailure" class="cs-wizard__operation-error" role="alert">
          <div class="cs-wizard__operation-error-head">
            <v-icon size="18" color="error">mdi-alert-circle-outline</v-icon>
            <span>{{ operationFailure.title }}</span>
          </div>
          <div class="cs-wizard__operation-error-message">{{ operationFailure.message }}</div>
          <div v-if="operationFailure.columns.length" class="cs-wizard__operation-columns">
            <v-chip
              v-for="column in operationFailure.columns"
              :key="column"
              x-small
              outlined
              color="error">
              {{ column }}
            </v-chip>
          </div>
        </div>

        <component
          :is="activeStepComponent"
          v-bind="stepProps"
          @name-input="nameError = null"
          @root-changed="onRootChanged"
          @bridge-target-changed="onBridgeTargetChanged"
          @retry-graph="syncGraph"
          @preview-verified="onPreviewVerified"
          @preview-invalidated="onPreviewInvalidated"
          @busy-change="onStepBusyChange"
          @definition-updated="onDefinitionUpdated" />
      </template>

      <div v-else class="cs-wizard__pending">
        <v-icon size="28" color="primary">mdi-progress-wrench</v-icon>
        <div class="cs-wizard__pending-text">
          {{ currentStepMeta.title }} arrives with the next delivery wave.
        </div>
      </div>

      <template #footer>
        <v-btn text :disabled="currentStep === 1 || saving || stepBusy" @click="goBack">Back</v-btn>

        <span v-if="footerMessage" class="cs-wizard__hint">{{ footerMessage }}</span>

        <v-spacer />

        <v-btn
          v-if="!factoryError"
          text
          :disabled="primaryActionDisabled"
          :loading="saving"
          @click="onPrimaryAction">
          {{ primaryActionLabel }}
        </v-btn>

        <v-btn
          v-if="!factoryError && currentStep < lastStep"
          color="primary"
          depressed
          :disabled="!canLeaveStep || saving || stepBusy"
          @click="goNext">
          Next
        </v-btn>
      </template>
    </wizard-shell>

    <!-- In-app confirm, never window.confirm: a browser modal is unstyleable, unautomatable and
         foreign to every other dialog in this product. -->
    <v-dialog v-model="confirmDiscardOpen" max-width="440" persistent>
      <v-card>
        <v-card-title class="cs-wizard__confirm-title">Discard unsaved changes?</v-card-title>
        <v-card-text>
          {{ unsavedSummary }} Closing now keeps only the last saved or applied version.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="saving || stepBusy" @click="confirmDiscardOpen = false">Keep editing</v-btn>
          <v-btn text color="error" :disabled="saving || stepBusy" @click="discardAndClose">
            Discard changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import WizardShell from '@/features/Share/components/wizard/WizardShell.vue';
  import { log, error } from '@/utils/debugLogger';
  import compositeSourceService from '../service/compositeSourceService';
  import registerService from '@/features/Registers/services/registerService';
  import taskManagerService from '@/features/Admin/features/TaskManager/service/TaskManager.service';
  import {
    CompositeSourceStatus,
    CompositeSourceCopy,
  } from '../config/compositeSourceEnums';
  import { CompositeNodeKind, humanizeNodeKind } from '../model/definitionVocabulary';
  import { createDefinitionModel, CompositeWizardStep } from '../model/definitionModel';
  import { rootKeyOf, toGraphRequest } from '../model/definitionModelHelpers';
  import { facilityWideRootOptions, withRootOptionKey } from '../model/rootOptions';
  import {
    authoringActionLabel,
    createLatestRequestGate,
    nextWizardStep,
    persistWorkingCopy,
    previousWizardStep,
    sameAuthoringSnapshot,
  } from '../model/authoringWorkflow';
  import {
    describeApiError,
    describeOperationFailure,
    conflictCodeOf,
    CompositeSourceConflict,
  } from './wizardErrors';
  import StepSource from './steps/StepSource.vue';
  import StepRelations from './steps/StepRelations.vue';
  import StepColumns from './steps/StepColumns.vue';
  import StepPreview from './steps/StepPreview.vue';
  import StepPublish from './steps/StepPublish.vue';

  /**
   * The composite-source authoring wizard: owns the definition model, the data every step reads,
   * and the local working copy every step edits. Navigation never writes. The author explicitly
   * saves a Draft or atomically applies a Published/Broken candidate.
   *
   * WHY STEP 1 DOES NOT USE THE GRAPH. It cannot — see above — so the candidate roots come from
   * the registers AND the task boards the target owns, fetched side by side. Those fetches are
   * the ONE place a source class is named, which is what kept DS-11 cheap: the boards call was
   * merged here and every step downstream was untouched, because a root travels as an opaque
   * `{kind, id, label}` and nothing below this component branches on a kind.
   *
   * The UI never filters roots by rules of its own. A register that has never been materialized
   * is shown, disabled, carrying the server's own reason — hiding it would leave a curator
   * hunting for something they know exists.
   */
  export default {
    name: 'CompositeSourceWizard',

    components: { WizardShell, StepSource, StepRelations, StepColumns, StepPreview, StepPublish },

    props: {
      /** v-model: dialog open state. */
      value: { type: Boolean, default: false },
      /** Selected workspace target: {kind, id, name}. */
      target: { type: Object, default: null },
      /** Row being edited, or null to create. Carries id, name, definitionJson, rowVersion. */
      definition: { type: Object, default: null },
    },

    data() {
      return {
        copy: CompositeSourceCopy,
        model: null,
        currentStep: CompositeWizardStep.Source,
        definitionId: null,
        rowVersion: null,
        // The row's lifecycle state and the view it owns. Tracked here because step 5 needs to
        // know whether it is looking at a Draft, Published or Broken row. Successful save,
        // apply, repair and publish responses move these values together.
        status: CompositeSourceStatus.Draft,
        viewName: null,
        graph: null,
        graphRootId: null,
        rootOptions: [],
        rootOptionsLoading: false,
        rootOptionsFilteredOut: 0,
        saving: false,
        stepBusy: false,
        confirmDiscardOpen: false,
        graphLoading: false,
        graphRequestGate: createLatestRequestGate(),
        operationFailure: null,
        previewedSnapshot: null,
        // What the definition looked like when it was last written. Compared against the live
        // model to know whether closing would throw work away.
        savedSnapshot: null,
        factoryError: null,
        nameError: null,
        lastStep: CompositeWizardStep.Publish,
        steps: [
          {
            title: 'Source',
            description: 'Name and starting data',
            heading: 'Name this source and pick its starting data',
            subheading: 'Everything else in this wizard hangs off the data you choose here.',
          },
          {
            title: 'Relations',
            description: 'Reach the records around it',
            heading: 'Reach the records around it',
            subheading: 'Add a relation for each set of records you want to fold in.',
          },
          {
            title: 'Columns',
            description: 'Choose what to project',
            heading: 'Choose what this source projects',
            subheading: 'Tick the columns to include, name them, and say which node supplies TagId.',
          },
          {
            title: 'Preview',
            description: 'Check the result',
            heading: 'Check what this source produces',
            subheading: 'A sample of the rows this source would return.',
          },
          {
            title: 'Finish',
            description: 'Save or apply this source',
            heading: 'Finish this source',
            subheading: 'Review the local definition, then save, apply, repair, or publish it.',
          },
        ],
      };
    },

    computed: {
      currentStepMeta() {
        return this.steps[this.currentStep - 1] || {};
      },

      registerOptions() {
        return this.rootOptions
          .filter((option) => option.kind === CompositeNodeKind.Register && option.selectable)
          .map((option) => ({ id: option.id, name: option.label }));
      },

      bridgeTargetId() {
        if (!this.model) return null;
        const bridge = this.model.state.edges.find((edge) => edge.target === 'registerRows');
        return bridge ? (bridge.targetId || null) : null;
      },

      activeStepComponent() {
        if (this.currentStep === CompositeWizardStep.Source) return StepSource;
        if (this.currentStep === CompositeWizardStep.Relations) return StepRelations;
        if (this.currentStep === CompositeWizardStep.Columns) return StepColumns;
        if (this.currentStep === CompositeWizardStep.Preview) return StepPreview;
        if (this.currentStep === CompositeWizardStep.Publish) return StepPublish;
        return null;
      },

      /**
       * Every step gets the same three: `{model, graph, target}`. Step 1 gets four more, and they
       * are passed ONLY to it on purpose — a
       * prop a component does not declare is applied to its root element as an attribute, so
       * handing the relations step an array of root options would stringify it into the DOM.
       */
      stepProps() {
        const uniform = { model: this.model, graph: this.graph, target: this.target };
        const readonly = false;

        if (this.currentStep === CompositeWizardStep.Source) {
          return {
            ...uniform,
            readonly,
            rootReadonly: !!this.definitionId,
            rootOptions: this.rootOptions,
            rootOptionsLoading: this.rootOptionsLoading,
            rootOptionsFilteredOut: this.rootOptionsFilteredOut,
            nameError: this.nameError,
          };
        }

        if (this.currentStep === CompositeWizardStep.Preview) {
          return uniform;
        }

        if (this.currentStep === CompositeWizardStep.Publish) {
          return {
            ...uniform,
            definitionId: this.definitionId,
            rowVersion: this.rowVersion,
            status: this.status,
            viewName: this.viewName,
            hasUnsavedChanges: this.hasUnsavedChanges,
            previewVerified: this.hasVerifiedPreview,
          };
        }

        if (this.currentStep === CompositeWizardStep.Relations) {
          return {
            ...uniform,
            readonly,
            graphLoading: this.graphLoading,
            registerOptions: this.registerOptions,
            registerOptionsLoading: this.rootOptionsLoading,
          };
        }

        if (this.currentStep === CompositeWizardStep.Columns) {
          return { ...uniform, readonly, graphLoading: this.graphLoading };
        }

        return { ...uniform, readonly };
      },

      stepValidity() {
        if (!this.model) return { valid: false, reason: null };
        return this.model.stepValidity(this.currentStep);
      },

      canLeaveStep() {
        return !!this.model && this.stepValidity.valid;
      },

      footerMessage() {
        if (this.factoryError) return null;
        if (!this.stepValidity.valid) return this.stepValidity.reason;
        if (this.isLiveCandidate && this.isFinishStep
          && this.canPersistWorkingCopy && !this.hasVerifiedPreview) {
          return 'Run Preview successfully for these exact changes before applying them.';
        }
        return null;
      },

      /**
       * Whether closing would lose work. Compared against the last WRITE rather than tracked with
       * a dirty flag, so it stays true to what the server actually holds however the author got
       * here — including relations added and then removed back to the saved shape, which is not
       * unsaved work and should not prompt.
       */
      hasUnsavedChanges() {
        if (!this.model || this.factoryError) return false;

        const current = this.currentSnapshot();
        if (!this.savedSnapshot) {
          // Nothing written yet: anything the author has entered is unsaved.
          return !!(current.name || current.json);
        }

        return current.name !== this.savedSnapshot.name || current.json !== this.savedSnapshot.json;
      },

      unsavedSummary() {
        return this.definitionId
          ? 'This source has changes that have not been saved.'
          : 'This source has never been saved.';
      },

      primaryActionLabel() {
        if (this.isLiveCandidate && !this.isFinishStep) return 'Review changes';
        return authoringActionLabel(this.status);
      },

      canPersistWorkingCopy() {
        return this.hasUnsavedChanges || this.status === CompositeSourceStatus.Broken;
      },

      isLiveCandidate() {
        return this.status === CompositeSourceStatus.Published
          || this.status === CompositeSourceStatus.Broken;
      },

      isFinishStep() {
        return this.currentStep === CompositeWizardStep.Publish;
      },

      hasVerifiedPreview() {
        return sameAuthoringSnapshot(this.currentSnapshot(), this.previewedSnapshot);
      },

      primaryActionDisabled() {
        return !this.canLeaveStep || this.saving || this.stepBusy || !this.canPersistWorkingCopy
          || (this.isLiveCandidate && this.isFinishStep && !this.hasVerifiedPreview);
      },
    },

    watch: {
      value(open) {
        if (open) this.start();
      },
    },

    created() {
      if (this.value) this.start();
    },

    methods: {
      /**
       * Begins an authoring session. Every session gets a fresh model — reusing one across opens
       * is how a previous source's edges end up in the next one.
       */
      start() {
        this.currentStep = CompositeWizardStep.Source;
        this.factoryError = null;
        this.nameError = null;
        this.graph = null;
        this.graphRootId = null;
        this.graphLoading = false;
        this.stepBusy = false;
        this.graphRequestGate.invalidate();
        this.operationFailure = null;
        this.previewedSnapshot = null;
        this.model = null;
        // Reset BEFORE the early returns below: a create session opened after an edit one would
        // otherwise inherit the previous row's id and save the new definition over it.
        this.definitionId = null;
        this.rowVersion = null;
        this.status = CompositeSourceStatus.Draft;
        this.viewName = null;

        if (!this.target) {
          this.factoryError = 'Choose a target before building a composite source.';
          return;
        }

        const existing = this.definition;

        try {
          this.model = createDefinitionModel(this.target, existing ? existing.definitionJson : null);
        } catch (err) {
          // The factory refuses a definition whose stored scope disagrees with the selected
          // target rather than silently rebinding it. That is a real situation — a group-owned
          // source is listed under a facility target — so it reads as an explanation, not a crash.
          error('[CompositeSourceWizard] could not open the definition', { err });
          this.factoryError = err && err.message;
          return;
        }

        if (existing) {
          // The name lives on the ROW, never inside definitionJson, so it is restored from the
          // row rather than from the document the model just parsed.
          this.model.setName(existing.name || '');
          this.definitionId = existing.id;
          this.rowVersion = existing.rowVersion;
          this.status = existing.status || CompositeSourceStatus.Draft;
          this.viewName = existing.compiledViewName || null;

        }

        // A freshly loaded definition is BY DEFINITION saved; without this an edit session would
        // prompt about unsaved changes the moment it opened.
        this.savedSnapshot = existing ? this.currentSnapshot() : null;

        log('[CompositeSourceWizard] session started', {
          targetId: this.target.id,
          definitionId: this.definitionId,
          mode: existing ? 'edit' : 'create',
        });

        this.fetchRootOptions();
        if (this.model.state.root) this.syncGraph();
      },

      /**
       * Candidate roots for the target: the registers it owns and the task boards it owns,
       * fetched side by side, then the facility-wide roots (AVA-560): one row per tag kind and
       * the facility's AI findings, which need no fetch (`facilityWideRootOptions`). The kind and the family header are stamped here, at the ONE place
       * that knows which endpoint an option came from; everything downstream treats both as
       * opaque data, and the chip label is DERIVED through the same helper the tree and diagram
       * use, so the word a curator reads for a kind is decided in exactly one place.
       *
       * FILTERED TO THE TARGET'S OWN, and that is load-bearing rather than tidy: BOTH endpoints
       * also return GROUP-owned entries — the register listing carries group registers (verified
       * on the wire), and `TaskTypes/ByFacility` deliberately includes the facility's parent
       * group's boards (TaskTypesController.cs:57-67). The compiler derives a definition's scope
       * from its ROOT's ownership, so a group-owned root under a facility target compiles to a
       * refusal; offering one would be offering a dead end. Global boards cannot appear here at
       * all — a board with neither facility nor group matches neither arm of that endpoint's
       * WHERE — and the engine refuses them as roots anyway. This mirrors the server's
       * root-pinning rules; it does not invent one.
       */
      async fetchRootOptions() {
        this.rootOptionsLoading = true;
        try {
          const [registersRes, boardsRes] = await Promise.all([
            registerService.getUserFacilityRegisters(this.target.id),
            taskManagerService.getByFacilityId(this.target.id),
          ]);

          const registers = (registersRes && registersRes.items) || [];
          const ownedRegisters = registers.filter((item) => item.facilityId === this.target.id);
          const boards = (boardsRes && boardsRes.data) || [];
          const ownedBoards = boards.filter((board) => board.facilityId === this.target.id);

          const registerKind = CompositeNodeKind.Register;
          const boardKind = CompositeNodeKind.TaskBoard;

          this.rootOptions = ownedRegisters.map((item) => withRootOptionKey({
            id: item.id,
            kind: registerKind,
            kindLabel: humanizeNodeKind(registerKind),
            family: this.copy.RegistersFamilyHeader,
            label: item.name,
            selectable: !!item.sqlViewName,
            unavailableReason: item.sqlViewName
              ? null
              : 'Never materialized, so it has no table to select from.',
          })).concat(ownedBoards.map((board) => withRootOptionKey({
            id: board.id,
            kind: boardKind,
            kindLabel: humanizeNodeKind(boardKind),
            family: this.copy.TaskBoardsFamilyHeader,
            label: board.name,
            // A board's rows are its live tasks; there is no materialization precondition.
            selectable: true,
            unavailableReason: null,
          }))).concat(facilityWideRootOptions());

          // Told apart so step 1 can say WHY it has nothing to offer: none exist at all, or the
          // ones that exist belong to a scope this source cannot be built on.
          this.rootOptionsFilteredOut = (registers.length - ownedRegisters.length)
            + (boards.length - ownedBoards.length);

          log('[CompositeSourceWizard] root options loaded', {
            targetId: this.target.id,
            returnedRegisters: registers.length,
            returnedBoards: boards.length,
            offered: this.rootOptions.length,
            filteredOutOfScope: this.rootOptionsFilteredOut,
            selectable: this.rootOptions.filter((o) => o.selectable).length,
          });

          this.resolveLoadedRootLabel();
        } catch (err) {
          error('[CompositeSourceWizard] failed to load root options', { err });
          // The tag and AI-finding rows need no fetch, so a failed register or board call
          // still leaves them to build on.
          this.rootOptions = facilityWideRootOptions();
          this.rootOptionsFilteredOut = 0;
          this.$dialog.notify.error('Failed to load the data this source could be built from', {
            position: 'top-right',
            timeout: 4000,
          });
        } finally {
          this.rootOptionsLoading = false;
        }
      },

      /**
       * Gives a loaded definition's root its display name.
       *
       * A stored definition names its root by id and nothing else — the document has no label
       * field — so an edit session opens with the tree and diagram calling the root by its kind.
       * The candidate list is the only thing that knows the name, and it arrives asynchronously,
       * so the label is applied when it lands.
       *
       * `setRoot` is the right call rather than a back door: re-selecting the SAME root refreshes
       * the label and deliberately does not clear the relations built on it.
       */
      resolveLoadedRootLabel() {
        const root = this.model && this.model.state.root;
        if (!root || root.label) return;

        const match = this.rootOptions.find((option) => option.key === rootKeyOf(root));
        if (!match) {
          log('[CompositeSourceWizard] root is not in the candidate list; keeping the kind label', {
            rootKind: root.kind,
            rootRegisterId: root.rootRegisterId,
          });
          return;
        }

        this.model.setRoot({ kind: root.kind, rootRegisterId: root.rootRegisterId, label: match.label });
      },

      /**
       * Fetches the authoring graph for the local root. Only the latest root generation may
       * update the wizard, so a slow response from an earlier selection is harmless.
       */
      async syncGraph() {
        const root = this.model && this.model.state.root;
        if (!root || !this.target) return;

        const bridgeTargetId = this.bridgeTargetId;
        const rootKey = `${rootKeyOf(root)}:${bridgeTargetId || ''}`;
        if (this.graph && this.graphRootId === rootKey) return;

        const requestGeneration = this.graphRequestGate.next();
        this.graphLoading = true;

        try {
          const graphRequest = toGraphRequest(root, bridgeTargetId);
          const res = await compositeSourceService.graphForRoot(
            graphRequest,
            this.target.id,
          );
          if (!this.graphRequestGate.isCurrent(requestGeneration)) return;

          this.graph = (res && res.data) || null;
          this.graphRootId = rootKey;
          log('[CompositeSourceWizard] graph loaded', {
            rootKind: root.kind,
            rootId: root.rootRegisterId,
            nodes: this.graph ? this.graph.nodes.length : 0,
            edges: this.graph ? this.graph.edges.length : 0,
            maxEdges: this.graph ? this.graph.maxEdges : null,
          });
        } catch (err) {
          if (!this.graphRequestGate.isCurrent(requestGeneration)) return;
          error('[CompositeSourceWizard] failed to load the graph', { err });
          this.graph = null;
          this.$dialog.notify.error(describeApiError(err, 'Failed to load what this source can reach'), {
            position: 'top-right',
            timeout: 5000,
          });
        } finally {
          if (this.graphRequestGate.isCurrent(requestGeneration)) this.graphLoading = false;
        }
      },

      /**
       * A different root invalidates the graph outright: it describes the old root's traversals
       * and columns, and the model has already dropped the relations built on them.
       */
      onRootChanged() {
        this.graphRequestGate.invalidate();
        this.graph = null;
        this.graphRootId = null;
        this.operationFailure = null;
        this.syncGraph();
      },

      onBridgeTargetChanged(targetId) {
        this.graphRequestGate.invalidate();
        this.graph = null;
        this.graphRootId = null;
        log('[CompositeSourceWizard] bridge target changed', { targetId });
        this.syncGraph();
      },

      onPreviewVerified(snapshot) {
        this.previewedSnapshot = snapshot;
      },

      onPreviewInvalidated() {
        this.previewedSnapshot = null;
      },

      onStepBusyChange(busy) {
        this.stepBusy = !!busy;
      },

      onPrimaryAction() {
        if (this.stepBusy) return false;
        if (this.isLiveCandidate && !this.isFinishStep) {
          this.currentStep = CompositeWizardStep.Publish;
          return true;
        }
        return this.saveWorkingCopy();
      },

      /**
       * Explicitly persists the local working copy. Drafts create/update; Published and Broken
       * use atomic candidate apply. No caller from Next/Back reaches this method.
       * @returns {Promise<boolean>} whether the write succeeded
       */
      async saveWorkingCopy() {
        // `target` is re-checked and not just trusted from `start()`: the workspace picker sits
        // behind this dialog and can be cleared while it is open.
        if (!this.model || !this.target || !this.canLeaveStep || this.saving || this.stepBusy) return false;
        if (this.isLiveCandidate && (!this.isFinishStep || !this.hasVerifiedPreview)) return false;

        this.saving = true;
        this.nameError = null;
        this.operationFailure = null;

        try {
          const definitionJson = this.model.toDefinitionJson();
          const name = this.model.state.name;
          const isFirstSave = !this.definitionId;
          const actionStatus = this.status;
          const res = await persistWorkingCopy({
            service: compositeSourceService,
            target: this.target,
            definitionId: this.definitionId,
            rowVersion: this.rowVersion,
            status: this.status,
            name,
            definitionJson,
          });
          const saved = res && res.data;

          this.absorbDefinition(saved);

          // Taken from what was SENT, not from the response: the server echoes the same
          // definitionJson back, and comparing against the sent text is what makes the next
          // "has anything changed since" question answerable without a re-read.
          this.savedSnapshot = { name, json: definitionJson };

          log('[CompositeSourceWizard] draft saved', {
            definitionId: this.definitionId,
            step: this.currentStep,
            created: isFirstSave,
          });

          const successMessage = isFirstSave
            ? 'Saved as draft'
            : {
              [CompositeSourceStatus.Draft]: 'Draft saved',
              [CompositeSourceStatus.Published]: 'Changes applied',
              [CompositeSourceStatus.Broken]: 'Source repaired',
            }[actionStatus];

          this.$dialog.notify.success(successMessage, {
            position: 'top-right',
            timeout: 3000,
          });

          this.$emit('saved', saved);
          return true;
        } catch (err) {
          this.reportSaveFailure(err);
          return false;
        } finally {
          this.saving = false;
        }
      },

      /**
       * Takes the server's word for the row's identity and state from any response that returns
       * the whole definition.
       *
       * Every field is guarded rather than assigned blindly: a partial response must not blank a
       * token the next If-Match depends on.
       */
      absorbDefinition(definition) {
        if (!definition) return;

        if (definition.id) this.definitionId = definition.id;
        if (definition.rowVersion) this.rowVersion = definition.rowVersion;
        if (definition.status) this.status = definition.status;
        this.viewName = definition.compiledViewName || null;
      },

      /**
       * A lifecycle action writes on the server and hands back the whole definition. The step
       * announces it; the wizard refreshes what it holds and lets the list know.
       *
       * This is a notification of a server write, not a step authoring data — the model is still
       * only ever changed through its own mutators.
       */
      onDefinitionUpdated(definition) {
        log('[CompositeSourceWizard] definition updated by a step', {
          definitionId: definition && definition.id,
          status: definition && definition.status,
        });

        this.absorbDefinition(definition);
        this.savedSnapshot = this.currentSnapshot();
        this.operationFailure = null;
        this.$emit('saved', definition);
      },

      /**
       * Puts a refusal where it can be acted on. A duplicate name belongs on the name field with
       * the author held on step 1 — a toast would leave them guessing which of five steps to fix.
       */
      reportSaveFailure(err) {
        error('[CompositeSourceWizard] save failed', { definitionId: this.definitionId, err });

        const conflict = conflictCodeOf(err);

        if (conflict === CompositeSourceConflict.NameTaken) {
          this.nameError = describeApiError(err, 'That name is already used.');
          this.currentStep = CompositeWizardStep.Source;
          return;
        }

        this.operationFailure = describeOperationFailure(
          err,
          'Failed to save this composite source. Your local changes are still here.',
        );
      },

      goNext() {
        if (!this.canLeaveStep || this.saving || this.stepBusy) return;
        this.currentStep = nextWizardStep(this.currentStep, this.lastStep);
      },

      goBack() {
        if (this.saving || this.stepBusy) return;
        this.currentStep = previousWizardStep(this.currentStep);
      },

      /**
       * The definition as it stands, in the two things a save writes. `json` is null before a
       * root is chosen, because the model refuses to serialize a rootless definition.
       */
      currentSnapshot() {
        if (!this.model) return { name: '', json: null };

        const name = this.model.state.name || '';
        if (!this.model.state.root) return { name, json: null };

        try {
          return { name, json: this.model.toDefinitionJson() };
        } catch (err) {
          error('[CompositeSourceWizard] could not snapshot the definition', { err });
          return { name, json: null };
        }
      },

      /** Asks before throwing work away. Every close funnels through here. */
      close() {
        if (this.saving || this.stepBusy) return;
        if (this.hasUnsavedChanges) {
          log('[CompositeSourceWizard] close blocked by unsaved changes', {
            definitionId: this.definitionId,
          });
          this.confirmDiscardOpen = true;
          return;
        }

        this.finishClose();
      },

      discardAndClose() {
        if (this.saving || this.stepBusy) return;
        log('[CompositeSourceWizard] unsaved changes discarded', { definitionId: this.definitionId });
        this.confirmDiscardOpen = false;
        this.finishClose();
      },

      finishClose() {
        log('[CompositeSourceWizard] closed', { definitionId: this.definitionId });
        this.graphRequestGate.invalidate();
        this.confirmDiscardOpen = false;
        this.$emit('close');
        this.$emit('input', false);
      },
    },
  };
</script>

<style scoped>
  .cs-wizard__blocked,
  .cs-wizard__pending {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    text-align: center;
    padding: 56px 24px;
  }

  .cs-wizard__blocked-title {
    font-family: var(--ds-font-family-heading);
    font-size: 1rem;
    font-weight: 600;
  }

  .cs-wizard__blocked-text,
  .cs-wizard__pending-text {
    font-size: 0.875rem;
    color: var(--v-text-base);
    max-width: 460px;
  }

  .cs-wizard__hint {
    font-size: 0.8125rem;
    color: var(--v-text-base);
    margin-left: 12px;
  }

  .cs-wizard__operation-error {
    padding: 10px 12px;
    margin-bottom: 12px;
    border: 1px solid var(--v-error-base);
    border-radius: var(--ds-radius-sm);
  }

  .cs-wizard__operation-error-head {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: var(--ds-font-family-heading);
    font-size: 0.875rem;
    font-weight: 600;
  }

  .cs-wizard__operation-error-message {
    margin-top: 4px;
    font-size: 0.8125rem;
    color: var(--v-text-base);
  }

  .cs-wizard__operation-columns {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 8px;
  }
</style>
